// ============================================================
// Datei: frontend/src/pages/MlDashboard.tsx
// ML Analysis Dashboard + Interaktiver Analyse-Chatbot
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { mlApi, tradesApi, accountsApi, MlAnalysis, Account, Trade, aiApi } from '../services/api';
import { useToast } from '../components/Toast';

const s = {
  page: { padding: 0 } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  badge: { background: '#8b5cf6', color: '#fff', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 } as React.CSSProperties,
  btnPrimary: { background: '#8b5cf6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 } as React.CSSProperties,
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 } as React.CSSProperties,
  card: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 20 } as React.CSSProperties,
  cardTitle: { color: '#9ca3af', fontSize: 13, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 } as React.CSSProperties,
  bigValue: { fontSize: 28, fontWeight: 700, color: '#fff' } as React.CSSProperties,
  subValue: { fontSize: 13, color: '#6b7280', marginTop: 2 } as React.CSSProperties,
  positive: { color: '#22c55e' } as React.CSSProperties,
  negative: { color: '#ef4444' } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, marginTop: 10 } as React.CSSProperties,
  th: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' as const, padding: '8px 10px', borderBottom: '1px solid #1e2539', textAlign: 'left' as const } as React.CSSProperties,
  td: { color: '#d1d5db', padding: '8px 10px', borderBottom: '1px solid #1e253933', fontSize: 13 } as React.CSSProperties,
  rec: { background: '#0d111788', border: '1px solid #1e253966', borderRadius: 8, padding: '10px 14px', marginBottom: 8, color: '#d1d5db', fontSize: 14, lineHeight: 1.5 } as React.CSSProperties,
  barBg: { background: '#1e2539', borderRadius: 4, height: 8, overflow: 'hidden', marginTop: 4 } as React.CSSProperties,
  barFill: (pct: number, color: string) => ({ background: color, height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 4, transition: 'width 0.5s' }) as React.CSSProperties,
  corrBadge: (corr: number) => ({
    display: 'inline-block', padding: '1px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    background: corr > 0.3 ? '#22c55e22' : corr < -0.3 ? '#ef444422' : '#6b728022',
    color: corr > 0.3 ? '#22c55e' : corr < -0.3 ? '#ef4444' : '#9ca3af',
  }) as React.CSSProperties,
  optimalCard: { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '2px solid #8b5cf6', borderRadius: 14, padding: 24, marginBottom: 20 } as React.CSSProperties,
  optTitle: { color: '#a78bfa', fontSize: 16, fontWeight: 700, marginBottom: 14 } as React.CSSProperties,
  optGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 } as React.CSSProperties,
  optItem: { background: '#0d111766', borderRadius: 8, padding: 12 } as React.CSSProperties,
  optLabel: { color: '#6b7280', fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  optValue: { color: '#fff', fontSize: 16, fontWeight: 700, marginTop: 2 } as React.CSSProperties,
  loading: { textAlign: 'center' as const, color: '#6b7280', padding: 60 } as React.CSSProperties,
  // Chatbot styles
  chatSection: { background: '#0f172a', border: '2px solid #8b5cf6', borderRadius: 16, marginBottom: 20, overflow: 'hidden' } as React.CSSProperties,
  chatHeader: { background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  chatHeaderTitle: { color: '#fff', fontSize: 18, fontWeight: 700 } as React.CSSProperties,
  chatHeaderSub: { color: '#c4b5fd', fontSize: 12 } as React.CSSProperties,
  chatBody: { height: 400, overflowY: 'auto' as const, padding: 20, display: 'flex', flexDirection: 'column' as const, gap: 12 } as React.CSSProperties,
  chatInputRow: { display: 'flex', gap: 10, padding: '12px 16px', borderTop: '1px solid #1e2539', background: '#0d1117' } as React.CSSProperties,
  chatInput: { flex: 1, background: '#1e293b', color: '#fff', border: '1px solid #334155', padding: '12px 16px', borderRadius: 10, fontSize: 14, outline: 'none' } as React.CSSProperties,
  chatSendBtn: { background: '#8b5cf6', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 } as React.CSSProperties,
  msgUser: { alignSelf: 'flex-end', background: '#8b5cf622', border: '1px solid #8b5cf644', color: '#e2e8f0', padding: '10px 16px', borderRadius: '14px 14px 4px 14px', maxWidth: '75%', fontSize: 14 } as React.CSSProperties,
  msgBot: { alignSelf: 'flex-start', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', padding: '12px 16px', borderRadius: '14px 14px 14px 4px', maxWidth: '85%', fontSize: 14, lineHeight: 1.6 } as React.CSSProperties,
  msgBotTyping: { alignSelf: 'flex-start', color: '#6b7280', fontSize: 14, padding: '10px 16px' } as React.CSSProperties,
  chipRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 8, marginTop: 12, padding: '0 20px 12px' } as React.CSSProperties,
  chip: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '6px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer', transition: 'all 0.2s' } as React.CSSProperties,
};

// ============================================================
// CHATBOT ANALYSIS ENGINE
// ============================================================
interface ChatMessage { role: 'user' | 'bot'; text: string; }

function analyzeQuestion(question: string, trades: Trade[], analysis: MlAnalysis | null): string {
  const q = question.toLowerCase().trim();
  const wins = trades.filter(t => t.pnl > 0);
  const losses = trades.filter(t => t.pnl < 0);
  const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
  const avgPnl = trades.length > 0 ? totalPnl / trades.length : 0;

  // Asset-spezifische Fragen
  const assetMatch = q.match(/(?:for|with|on|asset|at|bei|für|mit|auf)\s+(\w+)/);
  if (assetMatch || q.includes('asset') || q.includes('instrument')) {
    if (assetMatch) {
      const asset = assetMatch[1];
      const assetTrades = trades.filter(t => t.asset.toLowerCase() === asset);
      if (assetTrades.length === 0) {
        const available = Array.from(new Set(trades.map(t => t.asset))).join(', ');
        return `No trades found for "${asset}". Available assets: ${available}`;
      }
      const assetWins = assetTrades.filter(t => t.pnl > 0);
      const assetPnl = assetTrades.reduce((s, t) => s + t.pnl, 0);
      const assetAvg = assetPnl / assetTrades.length;
      return `📊 **${asset.toUpperCase()} Analyse:**\n` +
        `• Trades: ${assetTrades.length}\n` +
        `• Win Rate: ${(assetWins.length / assetTrades.length * 100).toFixed(1)}%\n` +
        `• Total PnL: $${assetPnl.toFixed(2)}\n` +
        `• Ø PnL: $${assetAvg.toFixed(2)}\n` +
        `• Bester Trade: $${Math.max(...assetTrades.map(t => t.pnl)).toFixed(2)}\n` +
        `• Schlechtester: $${Math.min(...assetTrades.map(t => t.pnl)).toFixed(2)}`;
    }
    // Allgemeine Asset-Übersicht
    const assets = Array.from(new Set(trades.map(t => t.asset)));
    const assetStats = assets.map(asset => {
      const at = trades.filter(t => t.asset === asset);
      const wr = at.filter(t => t.pnl > 0).length / at.length * 100;
      const tp = at.reduce((s, t) => s + t.pnl, 0);
      return { asset, count: at.length, winRate: wr, totalPnl: tp };
    }).sort((a, b) => b.totalPnl - a.totalPnl);

    return `📊 **Asset Ranking:**\n` +
      assetStats.map((a, i) => `${i + 1}. **${a.asset}** — ${a.count} Trades, WR: ${a.winRate.toFixed(1)}%, PnL: $${a.totalPnl.toFixed(2)}`).join('\n');
  }

  // Win Rate Fragen
  if (q.includes('win rate') || q.includes('winrate') || q.includes('gewinnrate') || q.includes('trefferquote')) {
    const wr = trades.length > 0 ? wins.length / trades.length * 100 : 0;
    let tip = wr >= 60 ? 'Great! Your win rate is above average.' :
              wr >= 50 ? 'Solid win rate. Optimize your risk/reward for more profit.' :
              'Unter 50% — fokussiere auf bessere Setups und weniger Trades.';
    return `📈 Deine Win Rate: **${wr.toFixed(1)}%** (${wins.length} Wins / ${losses.length} Losses)\n\n${tip}`;
  }

  // Session-Fragen
  if (q.includes('session') || q.includes('sitzung') || q.includes('ny') || q.includes('london') || q.includes('asia')) {
    const sessions = Array.from(new Set(trades.map(t => t.session)));
    const sessionStats = sessions.map(session => {
      const st = trades.filter(t => t.session === session);
      const wr = st.filter(t => t.pnl > 0).length / st.length * 100;
      const tp = st.reduce((s, t) => s + t.pnl, 0);
      return { session, count: st.length, winRate: wr, totalPnl: tp };
    }).sort((a, b) => b.totalPnl - a.totalPnl);

    return `🕐 **Session Performance:**\n` +
      sessionStats.map((s, i) => `${i + 1}. **${s.session}** — ${s.count} Trades, WR: ${s.winRate.toFixed(1)}%, PnL: $${s.totalPnl.toFixed(2)}`).join('\n') +
      `\n\n💡 Beste Session: **${sessionStats[0]?.session || '–'}**`;
  }

  // Drawdown-Fragen
  if (q.includes('drawdown') || q.includes('dd') || q.includes('verlust')) {
    const maxDd = Math.max(...trades.map(t => t.drawdown), 0);
    const streaks = computeStreaks(trades);
    return `📉 **Drawdown Analyse:**\n` +
      `• Max Drawdown: ${maxDd.toFixed(2)}%\n` +
      `• Longest losing streak: ${streaks.maxLoss} Trades\n` +
      `• Longest winning streak: ${streaks.maxWin} Trades\n` +
      `• Aktueller Streak: ${streaks.current > 0 ? `+${streaks.current} Wins ✅` : `${streaks.current} Losses ❌`}`;
  }

  // Risk/Reward Fragen
  if (q.includes('risk') || q.includes('reward') || q.includes('rr') || q.includes('crv')) {
    const rrTrades = trades.filter(t => t.risk_reward && t.risk_reward > 0);
    if (rrTrades.length === 0) return 'Keine Trades mit Risk/Reward Daten gefunden.';
    const avgRr = rrTrades.reduce((s, t) => s + (t.risk_reward || 0), 0) / rrTrades.length;
    const highRr = rrTrades.filter(t => (t.risk_reward || 0) >= 3);
    const highRrWin = highRr.filter(t => t.pnl > 0).length;
    return `⚖️ **Risk/Reward Analyse:**\n` +
      `• Ø R/R: ${avgRr.toFixed(1)}\n` +
      `• Trades mit RR ≥ 3: ${highRr.length} (Win Rate: ${highRr.length > 0 ? (highRrWin / highRr.length * 100).toFixed(1) : 0}%)\n` +
      (analysis ? `• Optimaler RR-Bereich: ${analysis.best_rr_range.optimal_min.toFixed(1)} – ${analysis.best_rr_range.optimal_max.toFixed(1)}` : '');
  }

  // Beste/Schlechteste Trades
  if (q.includes('bester') || q.includes('beste') || q.includes('top') || q.includes('best')) {
    const sorted = [...trades].sort((a, b) => b.pnl - a.pnl).slice(0, 5);
    return `🏆 **Top 5 Trades:**\n` +
      sorted.map((t, i) => `${i + 1}. **${t.asset}** (${t.session}) — $${t.pnl.toFixed(2)} | RR: ${t.risk_reward || '–'} | ${t.trade_date?.split('T')[0] || '–'}`).join('\n');
  }

  if (q.includes('schlechtester') || q.includes('schlechteste') || q.includes('worst') || q.includes('schlecht')) {
    const sorted = [...trades].sort((a, b) => a.pnl - b.pnl).slice(0, 5);
    return `💀 **Worst 5 Trades:**\n` +
      sorted.map((t, i) => `${i + 1}. **${t.asset}** (${t.session}) — $${t.pnl.toFixed(2)} | RR: ${t.risk_reward || '–'} | ${t.trade_date?.split('T')[0] || '–'}`).join('\n');
  }

  // PnL Fragen
  if (q.includes('pnl') || q.includes('profit') || q.includes('gewinn') || q.includes('verdien')) {
    return `💰 **PnL Overview:**\n` +
      `• Total PnL: $${totalPnl.toFixed(2)}\n` +
      `• Ø pro Trade: $${avgPnl.toFixed(2)}\n` +
      `• Ø Win: $${wins.length > 0 ? (wins.reduce((s, t) => s + t.pnl, 0) / wins.length).toFixed(2) : '0'}\n` +
      `• Ø Loss: $${losses.length > 0 ? (losses.reduce((s, t) => s + t.pnl, 0) / losses.length).toFixed(2) : '0'}\n` +
      `• Bester: $${Math.max(...trades.map(t => t.pnl), 0).toFixed(2)}\n` +
      `• Schlechtester: $${Math.min(...trades.map(t => t.pnl), 0).toFixed(2)}`;
  }

  // Dauer / Duration
  if (q.includes('dauer') || q.includes('duration') || q.includes('zeit') || q.includes('schnell') || q.includes('lang')) {
    const avgDur = trades.reduce((s, t) => s + t.duration_mins, 0) / trades.length;
    const short = trades.filter(t => t.duration_mins <= 15);
    const long = trades.filter(t => t.duration_mins > 60);
    const shortWr = short.length > 0 ? short.filter(t => t.pnl > 0).length / short.length * 100 : 0;
    const longWr = long.length > 0 ? long.filter(t => t.pnl > 0).length / long.length * 100 : 0;
    return `⏱️ **Dauer-Analyse:**\n` +
      `• Ø Dauer: ${avgDur.toFixed(0)} Minuten\n` +
      `• Kurze Trades (≤15m): ${short.length} — WR: ${shortWr.toFixed(1)}%\n` +
      `• Lange Trades (>60m): ${long.length} — WR: ${longWr.toFixed(1)}%\n` +
      `• ${shortWr > longWr ? '💡 Short trades perform better!' : '💡 Longer trades perform better!'}`;
  }

  // Empfehlung / Tipp
  if (q.includes('empfehlung') || q.includes('tipp') || q.includes('verbessern') || q.includes('optimier') || q.includes('ratschlag')) {
    if (!analysis) return 'Press "Re-analyze" first to generate recommendations.';
    return `💡 **Recommendations basierend auf deinen Trades:**\n\n` +
      analysis.recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n\n');
  }

  // Summary / Überblick
  if (q.includes('zusammenfassung') || q.includes('summary') || q.includes('overview') || q.includes('status')) {
    const wr = trades.length > 0 ? wins.length / trades.length * 100 : 0;
    return `📋 **Trading Summary:**\n` +
      `• Trades gesamt: ${trades.length}\n` +
      `• Win Rate: ${wr.toFixed(1)}% (${wins.length}W / ${losses.length}L)\n` +
      `• Total PnL: $${totalPnl.toFixed(2)}\n` +
      `• Ø pro Trade: $${avgPnl.toFixed(2)}\n` +
      `• Assets: ${Array.from(new Set(trades.map(t => t.asset))).join(', ')}\n` +
      `• Sessions: ${Array.from(new Set(trades.map(t => t.session))).join(', ')}\n` +
      (analysis ? `• Bestes Asset: ${analysis.best_asset.asset}\n• Beste Session: ${analysis.best_session.session}` : '');
  }

  // Tags
  if (q.includes('tag') || q.includes('hashtag') || q.includes('label')) {
    const allTags = trades.flatMap(t => t.tags || []);
    if (allTags.length === 0) return '🏷️ No tags created yet. Add tags to your trades for tag analysis!';
    const tagCounts: Record<string, { count: number, pnl: number, wins: number }> = {};
    trades.forEach(t => (t.tags || []).forEach(tag => {
      if (!tagCounts[tag]) tagCounts[tag] = { count: 0, pnl: 0, wins: 0 };
      tagCounts[tag].count++;
      tagCounts[tag].pnl += t.pnl;
      if (t.pnl > 0) tagCounts[tag].wins++;
    }));
    const tagList = Object.entries(tagCounts).sort((a, b) => b[1].pnl - a[1].pnl);
    return `🏷️ **Tag Performance:**\n` +
      tagList.map(([tag, d]) => `• **#${tag}** — ${d.count} Trades, WR: ${(d.wins / d.count * 100).toFixed(1)}%, PnL: $${d.pnl.toFixed(2)}`).join('\n');
  }

  // Custom Fields
  if (q.includes('custom') || q.includes('feld') || q.includes('delta') || q.includes('volum')) {
    if (!analysis || analysis.custom_field_insights.length === 0) return '📊 No custom fields analyzed yet. Add fields like "delta", "volume" etc. to your trades!';
    return `📊 **Custom Field Insights:**\n` +
      analysis.custom_field_insights.map(ci =>
        `• **${ci.field_name}**: Correlation ${ci.correlation_with_pnl > 0 ? '+' : ''}${ci.correlation_with_pnl.toFixed(3)} | ` +
        `Optimal: ${ci.optimal_range ? `${ci.optimal_range[0]}–${ci.optimal_range[1]}` : '–'} | ${ci.description}`
      ).join('\n');
  }

  // Help
  if (q.includes('hilfe') || q.includes('help') || q.includes('was kannst') || q.includes('fragen')) {
    return `🤖 **Ich kann dir folgende Fragen beantworten:**\n\n` +
      `📈 "Win Rate" — Deine Trefferquote\n` +
      `💰 "PnL / Profit" — Profit overview\n` +
      `📊 "Asset btc" — Performance eines Assets\n` +
      `🕐 "Sessions" — Welche Session ist am besten\n` +
      `📉 "Drawdown" — Verlust-Analyse & Streaks\n` +
      `⚖️ "Risk Reward" — R/R Optimierung\n` +
      `🏆 "Best Trades" — Top 5 Trades\n` +
      `💀 "Schlechteste" — Worst 5 Trades\n` +
      `⏱️ "Dauer" — Trade-Duration Analyse\n` +
      `🏷️ "Tags" — Tag Performance\n` +
      `📊 "Custom Fields" — Feld-Correlationen\n` +
      `💡 "Recommendations" — Optimierungstipps\n` +
      `📋 "Summary" — Full overview`;
  }

  // Fallback
  return `I'm not sure what you mean. Try:\n• "Summary" for an overview\n• "Win Rate" for your hit rate\n• "Asset btc" for a specific asset\n• "Help" for all options`;
}

function computeStreaks(trades: Trade[]) {
  let maxWin = 0, maxLoss = 0, current = 0, curWin = 0, curLoss = 0;
  for (const t of trades) {
    if (t.pnl > 0) { curWin++; curLoss = 0; maxWin = Math.max(maxWin, curWin); }
    else { curLoss++; curWin = 0; maxLoss = Math.max(maxLoss, curLoss); }
  }
  current = curWin > 0 ? curWin : -curLoss;
  return { maxWin, maxLoss, current };
}

// ============================================================
// COMPONENT
// ============================================================
const MlDashboard: React.FC = () => {
  const toast = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [analysis, setAnalysis] = useState<MlAnalysis | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [field1, setField1] = useState('');
  const [field2, setField2] = useState('');
  const [queryResult, setQueryResult] = useState<any>(null);
  const [queryLoading, setQueryLoading] = useState(false);

  // Chatbot state
  const [aiLoading, setAiLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    { role: 'bot', text: '🤖 Hello! I am your trading analysis bot. Ask me about your trades — e.g. "Win Rate", "best asset", "Summary" or "Help" for all options.' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatTyping, setChatTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const accRes = await accountsApi.list();
      const active = accRes.data.find(a => a.is_active) || accRes.data[0];
      if (!active) { setError('No account found'); return; }
      setAccount(active);

      const [analysisRes, tradesRes] = await Promise.allSettled([
        mlApi.analyze(active.id),
        tradesApi.list({ account_id: active.id }),
      ]);

      if (analysisRes.status === 'fulfilled') setAnalysis(analysisRes.value.data);
      if (tradesRes.status === 'fulfilled') setTrades(tradesRes.value.data);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
      toast.error('ML analysis failed', 'ML analysis could not be loaded. Make sure the ML endpoint is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const runQuery = async () => {
    if (!account || !field1) return;
    setQueryLoading(true);
    try {
      const res = field2
        ? await mlApi.queryCombo(account.id, field1, field2)
        : await mlApi.queryField(account.id, field1);
      setQueryResult(res.data);
    } catch { setQueryResult({ error: 'Query failed' }); }
    finally { setQueryLoading(false); }
  };

  const sendChatMessage = () => {
    const msg = chatInput.trim();
    if (!msg) return;
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setChatTyping(true);

    setTimeout(() => {
      const response = analyzeQuestion(msg, trades, analysis);
      setChatMessages(prev => [...prev, { role: 'bot', text: response }]);
      setChatTyping(false);
    }, 400 + Math.random() * 600);
  };

  const quickQuestion = (q: string) => {
    setChatInput(q);
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setChatTyping(true);
    setTimeout(() => {
      const response = analyzeQuestion(q, trades, analysis);
      setChatMessages(prev => [...prev, { role: 'bot', text: response }]);
      setChatTyping(false);
    }, 400 + Math.random() * 600);
  };

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, i) => {
      const formatted = line
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/• /g, '&nbsp;&nbsp;• ');
      return <div key={i} dangerouslySetInnerHTML={{ __html: formatted }} style={{ marginBottom: line === '' ? 8 : 2 }} />;
    });
  };

  if (loading) return <div style={s.loading}>⏳ Loading analysis...</div>;
  if (error) return <div style={s.loading}>❌ {error}</div>;

  const a = analysis;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          🤖 ML Analysis
          <span style={s.badge}>{account?.name}</span>
          <span style={{ fontSize: 14, fontWeight: 400, color: '#6b7280' }}>
            {a?.total_trades_analyzed || trades.length} trades analyzed
          </span>
        </h1>
        <button style={s.btnPrimary} onClick={loadData}>🔄 Re-analyze</button>
      </div>

      {/* === CHATBOT === */}
      <div style={s.chatSection}>
        <div style={s.chatHeader}>
          <span style={{ fontSize: 28 }}>🤖</span>
          <div>
            <div style={s.chatHeaderTitle}>Trading Analysis Bot</div>
            <div style={s.chatHeaderSub}>AI Trading Bot — Set API key in Settings for real AI insights</div>
          </div>
        </div>

        <div style={s.chatBody} id="chatBody">
          {chatMessages.map((msg, i) => (
            <div key={i} style={msg.role === 'user' ? s.msgUser : s.msgBot}>
              {msg.role === 'bot' ? renderMarkdown(msg.text) : msg.text}
            </div>
          ))}
          {chatTyping && <div style={s.msgBotTyping}>🤖 analyzing...</div>}
          <div ref={chatEndRef} />
        </div>

        {/* Quick Questions */}
        <div style={s.chipRow}>
          {['Summary', 'Win Rate', 'Best Trades', 'Sessions', 'Drawdown', 'Recommendations'].map(q => (
            <span key={q} style={s.chip} onClick={() => quickQuestion(q)}
              onMouseEnter={e => { (e.target as any).style.background = '#334155'; (e.target as any).style.color = '#fff'; }}
              onMouseLeave={e => { (e.target as any).style.background = '#1e293b'; (e.target as any).style.color = '#94a3b8'; }}>
              {q}
            </span>
          ))}
        </div>

        <div style={s.chatInputRow}>
          <input
            style={s.chatInput}
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') sendChatMessage(); }}
            placeholder="Ask me something... z.B. 'What is my win rate on btc?'"
          />
          <button style={s.chatSendBtn} onClick={sendChatMessage} disabled={!chatInput.trim()}>
            Send ➤
          </button>
        </div>
      </div>

      {/* === ANALYSIS RESULTS === */}
      {a && (
        <>
          {/* Optimal Profile */}
          <div style={s.optimalCard}>
            <div style={s.optTitle}>✨ Optimal Trade Profile</div>
            <div style={s.optGrid}>
              <div style={s.optItem}><div style={s.optLabel}>BEST ASSET</div><div style={s.optValue}>{a.best_asset.asset}</div></div>
              <div style={s.optItem}><div style={s.optLabel}>BEST SESSION</div><div style={s.optValue}>{a.best_session.session}</div></div>
              <div style={s.optItem}><div style={s.optLabel}>OPTIMAL RR</div><div style={s.optValue}>{a.best_rr_range.optimal_min.toFixed(1)}-{a.best_rr_range.optimal_max.toFixed(1)}</div></div>
              <div style={s.optItem}><div style={s.optLabel}>EXPECTED WIN RATE</div><div style={{ ...s.optValue, ...s.positive }}>{a.optimal_trade_profile.expected_win_rate.toFixed(1)}%</div></div>
              <div style={s.optItem}><div style={s.optLabel}>EXPECTED AVG PNL</div><div style={{ ...s.optValue, ...s.positive }}>${a.optimal_trade_profile.expected_avg_pnl.toFixed(2)}</div></div>
              <div style={s.optItem}><div style={s.optLabel}>BEST TAGS</div><div style={s.optValue}>{a.optimal_trade_profile.best_tags.length > 0 ? a.optimal_trade_profile.best_tags.join(', ') : 'No tags yet'}</div></div>
            </div>
          </div>

          {/* Asset + Session Performance */}
          <div style={s.grid2}>
            <div style={s.card}>
              <div style={s.cardTitle}>📊 Asset Performance</div>
              <div className="table-scroll">
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>ASSET</th><th style={s.th}>TRADES</th><th style={s.th}>WIN RATE</th><th style={s.th}>Ø PNL</th><th style={s.th}>SHARPE</th>
                </tr></thead>
                <tbody>
                  {a.best_asset.all_assets.map(asset => (
                    <tr key={asset.asset}>
                      <td style={{ ...s.td, fontWeight: 600 }}>
                        {asset.asset} {asset.asset === a.best_asset.asset && <span style={{ color: '#fbbf24' }}>⭐</span>}
                      </td>
                      <td style={s.td}>{asset.total_trades}</td>
                      <td style={s.td}>
                        <span style={asset.win_rate >= 50 ? s.positive : s.negative}>{asset.win_rate.toFixed(1)}%</span>
                        <div style={s.barBg}><div style={s.barFill(asset.win_rate, asset.win_rate >= 50 ? '#22c55e' : '#6b7280')} /></div>
                      </td>
                      <td style={{ ...s.td, ...(asset.avg_pnl >= 0 ? s.positive : s.negative) }}>${asset.avg_pnl.toFixed(2)}</td>
                      <td style={s.td}>{asset.sharpe.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>🕐 Session Performance</div>
              <div className="table-scroll">
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>SESSION</th><th style={s.th}>TRADES</th><th style={s.th}>WIN RATE</th><th style={s.th}>Ø PNL</th><th style={s.th}>TOTAL PNL</th>
                </tr></thead>
                <tbody>
                  {a.best_session.all_sessions.map(sess => (
                    <tr key={sess.session}>
                      <td style={{ ...s.td, fontWeight: 600 }}>
                        {sess.session} {sess.session === a.best_session.session && <span style={{ color: '#fbbf24' }}>⭐</span>}
                      </td>
                      <td style={s.td}>{sess.total_trades}</td>
                      <td style={s.td}>
                        <span style={sess.win_rate >= 50 ? s.positive : s.negative}>{sess.win_rate.toFixed(1)}%</span>
                        <div style={s.barBg}><div style={s.barFill(sess.win_rate, sess.win_rate >= 50 ? '#22c55e' : '#6b7280')} /></div>
                      </td>
                      <td style={{ ...s.td, ...(sess.avg_pnl >= 0 ? s.positive : s.negative) }}>${sess.avg_pnl.toFixed(2)}</td>
                      <td style={{ ...s.td, ...(sess.total_pnl >= 0 ? s.positive : s.negative) }}>${sess.total_pnl.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* RR + Tags */}
          <div style={s.grid2}>
            <div style={s.card}>
              <div style={s.cardTitle}>🔀 Risk-Reward Analysis</div>
              <div className="table-scroll">
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>RANGE</th><th style={s.th}>TRADES</th><th style={s.th}>WIN RATE</th><th style={s.th}>Ø PNL</th>
                </tr></thead>
                <tbody>
                  {a.best_rr_range.ranges.map(r => (
                    <tr key={r.range_label}>
                      <td style={{ ...s.td, fontWeight: 600, color: r.range_label.includes(`${a.best_rr_range.optimal_min}`) ? '#fbbf24' : '#d1d5db' }}>
                        {r.range_label} {r.range_label.includes(`${a.best_rr_range.optimal_min}`) && '⭐'}
                      </td>
                      <td style={s.td}>{r.trade_count}</td>
                      <td style={{ ...s.td, ...(r.win_rate >= 50 ? s.positive : s.negative) }}>{r.win_rate.toFixed(1)}%</td>
                      <td style={{ ...s.td, ...(r.avg_pnl >= 0 ? s.positive : s.negative) }}>${r.avg_pnl.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>🏷️ Tag Performance</div>
              {a.tag_performance.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13, padding: 10 }}>No tags created yet</div>
              ) : (
                <div className="table-scroll">
                <table style={s.table}>
                  <thead><tr>
                    <th style={s.th}>TAG</th><th style={s.th}>TRADES</th><th style={s.th}>WIN RATE</th><th style={s.th}>Ø PNL</th><th style={s.th}>TOTAL</th>
                  </tr></thead>
                  <tbody>
                    {a.tag_performance.map(tp => (
                      <tr key={tp.tag}>
                        <td style={{ ...s.td, fontWeight: 600 }}>#{tp.tag}</td>
                        <td style={s.td}>{tp.trade_count}</td>
                        <td style={{ ...s.td, ...(tp.win_rate >= 50 ? s.positive : s.negative) }}>{tp.win_rate.toFixed(1)}%</td>
                        <td style={{ ...s.td, ...(tp.avg_pnl >= 0 ? s.positive : s.negative) }}>${tp.avg_pnl.toFixed(2)}</td>
                        <td style={{ ...s.td, ...(tp.total_pnl >= 0 ? s.positive : s.negative) }}>${tp.total_pnl.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>

          {/* Custom Field Insights */}
          {a.custom_field_insights.length > 0 && (
            <div style={{ ...s.card, marginBottom: 20 }}>
              <div style={s.cardTitle}>📊 Custom Field Insights</div>
              <div className="table-scroll">
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>FELD</th><th style={s.th}>KORRELATION</th><th style={s.th}>OPTIMALER BEREICH</th><th style={s.th}>Ø PNL IM OPTIMUM</th><th style={s.th}>BESCHREIBUNG</th>
                </tr></thead>
                <tbody>
                  {a.custom_field_insights.map(ci => (
                    <tr key={ci.field_name}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{ci.field_name}</td>
                      <td style={s.td}>
                        <span style={s.corrBadge(ci.correlation_with_pnl)}>
                          {ci.correlation_with_pnl > 0 ? '+' : ''}{ci.correlation_with_pnl.toFixed(3)}
                        </span>
                      </td>
                      <td style={s.td}>{ci.optimal_range ? `${ci.optimal_range[0]} – ${ci.optimal_range[1]}` : '–'}</td>
                      <td style={{ ...s.td, ...(ci.avg_pnl_at_best >= 0 ? s.positive : s.negative) }}>${ci.avg_pnl_at_best.toFixed(2)}</td>
                      <td style={{ ...s.td, fontSize: 12 }}>{ci.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div style={{ ...s.card, marginBottom: 20 }}>
            <div style={s.cardTitle}>💡 Recommendations</div>
            {a.recommendations.map((rec, idx) => (
              <div key={idx} style={s.rec}>{rec}</div>
            ))}
          </div>
        </>
      )}

      {/* Field Query */}
      <div style={{ ...s.card, marginBottom: 20 }}>
        <div style={s.cardTitle}>🔍 Field Analysis (API Query)</div>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
          Query custom field correlations directly via the backend API.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input style={{ ...s.chatInput, flex: 1 }} value={field1} onChange={e => setField1(e.target.value)} placeholder="Field 1 (e.g. delta, rr)" />
          <input style={{ ...s.chatInput, flex: 1 }} value={field2} onChange={e => setField2(e.target.value)} placeholder="Field 2 (optional)" />
          <button style={s.btnPrimary} onClick={runQuery} disabled={queryLoading || !field1}>
            {queryLoading ? '⏳...' : '🔍 Query'}
          </button>
        </div>
        {queryResult && (
          <pre style={{ background: '#0d1117', border: '1px solid #1e2539', borderRadius: 8, padding: 14, marginTop: 14, fontSize: 12, color: '#d1d5db', maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(queryResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default MlDashboard;